import {
  ComponentType,
  FocusEvent,
  HTMLAttributes,
  KeyboardEvent
} from 'react';

export const TABBABLE_CLASS = '__TABBABLE__';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

interface TabbableProps extends HTMLAttributes<InputElement> {
  /**
   * @description Called when the user presses the escape key
   */
  onCancel?: (event: KeyboardEvent) => void;
  /**
   * @description Called when the user tabs, presses enter or navigates with the arrow keys
   */
  onFinished?: (event: FocusEvent | KeyboardEvent) => void;
  /**
   * @description Restrain the tabbable components to a certain parent class.
   */
  parentClass?: string;
}

// TODO: allow horizontal navigation with a row of inputs when pressing Tab
function handleNavigation(
  event: KeyboardEvent<InputElement>,
  parentClass = '',
  direction: 'forward' | 'backward' = 'forward'
): void {
  event.preventDefault();
  event.stopPropagation();

  const rootContainer = parentClass ? `.${parentClass}` : '';

  const notDisabled = ':not(:disabled)';
  const fields = document.querySelectorAll(`
    ${rootContainer} .${TABBABLE_CLASS} input${notDisabled},
    ${rootContainer} .${TABBABLE_CLASS} texarea${notDisabled},
    ${rootContainer} input${notDisabled}.${TABBABLE_CLASS},
    ${rootContainer} texarea${notDisabled}.${TABBABLE_CLASS}
  `);

  const i = [...fields].indexOf(event.target as InputElement);

  const nextSibling = (fields[i + 1] || fields[0]) as InputElement;
  const previousSibling = (fields[i - 1] ||
    fields[fields.length - 1]) as InputElement;
  const sibling = direction === 'forward' ? nextSibling : previousSibling;

  sibling?.focus();
  sibling?.setSelectionRange?.(0, sibling?.value?.length || 0);
}

function handleCancel(
  event: KeyboardEvent<InputElement> | FocusEvent<InputElement>
) {
  event.preventDefault();
  event.stopPropagation();
  (event.target as InputElement).blur();
}

function withTabbable<T>(InputComponent: ComponentType<T & TabbableProps>) {
  type InputComponentProps = T & TabbableProps;

  const displayName =
    InputComponent.displayName || InputComponent.name || 'InputComponent';

  const ComponentWithTabbable = (props: InputComponentProps) => {
    const { className = '', onFinished, onCancel, parentClass } = props;

    const onKeyDown = (event): void => {
      switch (event.key) {
        case 'Enter':
        case 'Tab':
          handleNavigation(event, parentClass);
          break;
        case 'ArrowUp':
          handleNavigation(event, parentClass, 'backward');
          break;
        case 'ArrowDown':
          handleNavigation(event, parentClass, 'forward');
          break;
        case 'Escape':
          handleCancel(event);
          onCancel?.(event);
          return;
        default:
          return;
      }
    };

    const onBlur = (event: FocusEvent<InputElement>) => {
      onFinished?.(event);
    };

    return (
      <InputComponent
        {...(props as T)}
        className={`${className} ${TABBABLE_CLASS}`}
        onKeyDown={onKeyDown}
        onBlur={
          (event: FocusEvent<InputElement>) =>
            setTimeout(() => onBlur(event), 100) // Force to end of call stack to prevent interference with onKeyDown
        }
        onFocus={(event: FocusEvent<InputElement>) => {
          event.target.setSelectionRange(0, event.target.value.length);
          props.onFocus?.(event);
        }}
      />
    );
  };

  ComponentWithTabbable.displayName = `withTabbable(${displayName})`;

  return ComponentWithTabbable;
}

export { withTabbable };
